<template>
  <tm-button-toggle
    ref="tooltipAnchor"
    class="compose-toggle"
    :model-value="value"
    :options="options"
    :spread="spread"
    bordered
    rounded
    @update:model-value="onModelValueUpdate"
  >
    <template v-slot:sms>
      <compose-toggle-option
        :label="$t('composeHeader.sms')"
        icon="chat"
      />
    </template>

    <template v-slot:email>
      <div class="d-flex nowrap">
        <compose-toggle-option
          :label="$t('composeHeader.email')"
          icon="email"
        />
      </div>
    </template>

    <compose-toggle-tooltip
      v-if="isAllowFeatureTooltip && showFeatureTooltipByTimer"
      :target="tooltipAnchor?.$el"
      position="left"
      arrow-vertical-position="top"
    />
  </tm-button-toggle>
</template>

<script setup lang="ts">
import TmButtonToggle from '@/components/shared/TmButtonToggle.vue'
import { composeEmailRoute, composeMessageRouteName } from '@/routes/user/compose'
import type { TmButtonToggleOption } from '@/components/shared/types'
import ComposeToggleOption from '@/components/domain/compose/ComposeToggleOption.vue'
import { useBreakpoints } from '@/composition/breakpoints'
import {
  getEmailCampaignDomainService,
  getEmailCampaignDomainsPreloaderService,
  getLocalStorageService,
  getUiSettingsService,
} from '@/core/container/ioc'
import { computed, onBeforeUnmount, onMounted, ref, watch } from '@/composition/vue/compositionApi'
import { COMPOSE_STORAGE_KEYS } from '@/services/domain/compose/const'
import type { ComposeToggleValue } from '@/services/domain/compose/types'
import { useRoutePrefetch } from '@/composition/useRoutePrefetch'
import type { StylesheetSizeProp } from '@/types'
import ComposeToggleTooltip from '@/components/domain/compose/ComposeToggleTooltip.vue'
import { useFeatureTooltipWasShown } from '@/composition/compose/useFeatureTooltipWasShown'
import { handleFormError } from '@/composition/forms'

const TOOLTIP_DELAY = 5000

const props = defineProps<{
  value: ComposeToggleValue
  spread?: boolean
  buttonHeight?: StylesheetSizeProp
}>()

const emit = defineEmits<{
  'update:modelValue': [value: ComposeToggleValue]
}>()

let timerId: ReturnType<typeof setTimeout>

const tooltipAnchor = ref<{ $el: HTMLElement }>()

const options: TmButtonToggleOption<ComposeToggleValue>[] = [
  {
    value: 'sms',
    to: { name: composeMessageRouteName },
    slot: 'sms',
  },
  {
    value: 'email',
    to: { name: composeEmailRoute.name },
    slot: 'email',
  },
]

useRoutePrefetch(options.map((option) => option.to?.name))

const { isLargeLaptopMin } = useBreakpoints()
const localStorageService = getLocalStorageService()
const emailCampaignDomainsPreloaderService = getEmailCampaignDomainsPreloaderService()

const isDomainsLoaded = ref(false)
emailCampaignDomainsPreloaderService.preload().then(() => {
  isDomainsLoaded.value = true
})
const domains = computed(() => getEmailCampaignDomainService().findAll())
const composeSwitchChannelsTooltipHidden = computed(
  () => getUiSettingsService().firstOrFail().composeSwitchChannelsTooltipHidden,
)

const showFeatureTooltipByTimer = ref(false)

const { isFeatureTooltipWasShown } = useFeatureTooltipWasShown()

const isAllowFeatureTooltip = computed(() => {
  return (
    isDomainsLoaded.value &&
    !domains.value.length &&
    props.value === 'sms' &&
    isLargeLaptopMin.value &&
    !composeSwitchChannelsTooltipHidden.value
  )
})

const saveToggleValue = (value: ComposeToggleValue): void => {
  localStorageService.set(COMPOSE_STORAGE_KEYS.SELECTED_TYPE, value)
}

const onModelValueUpdate = (value: ComposeToggleValue) => {
  saveToggleValue(value)
  hideFeatureTooltip()
  emit('update:modelValue', value)
}

onMounted(() => {
  saveToggleValue(props.value)
  emit('update:modelValue', props.value)
})

onBeforeUnmount(() => {
  clearTimeout(timerId)
})

const buttonHeightCssProp = computed<StylesheetSizeProp | ''>(() => props.buttonHeight ?? '')

const hideFeatureTooltip = () => {
  isFeatureTooltipWasShown.value = true
  showFeatureTooltipByTimer.value = false
  clearTimeout(timerId)
}

watch(
  isAllowFeatureTooltip,
  (newVal) => {
    clearTimeout(timerId)
    if (newVal && !isFeatureTooltipWasShown.value) {
      showFeatureTooltipByTimer.value = true
      timerId = setTimeout(async () => {
        hideFeatureTooltip()
        try {
          await getUiSettingsService().updateSettings({ composeSwitchChannelsTooltipHidden: true })
        } catch (e) {
          handleFormError(e)
        }
      }, TOOLTIP_DELAY)
    }
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.ml-6px {
  margin-left: 6px;
}
.compose-toggle {
  --tm-button-toggle-height: v-bind('buttonHeightCssProp');
  position: relative;
}
</style>
